
import React from 'react'
import { motion } from 'motion/react'
import { useParams } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { useCanvas } from '@/hooks/useCanvas'
import { useSharedProjects } from '@/providers/ProjectsProvider'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { CanvasCard, CanvasCardSkeleton } from '@/components/workspace/card'
import { useCanvasModals } from '@/components/workspace/hooks'
import { CanvasActionModals } from '@/components/workspace/list/CanvasActionModals'
import { CanvasListFooter } from '@/components/workspace/list/CanvasListFooter'
import NotFound from '@/components/workspace/shared/not-found'

/** Sessions per request. The grid is four wide at its widest, so a page fills whole rows. */
const PAGE_SIZE = 24

export function ProjectPage() {
  const { uuid: projectUuid } = useParams({ strict: false }) as { uuid: string }

  // The project is the query, not a filter applied to whatever happened to load: the server
  // returns this project's sessions ordered and paginated, so old ones are reachable too.
  const {
    canvases: projectCanvases,
    total,
    hasMore,
    isLoading: canvasesLoading,
    isLoadingMore,
    error: canvasesError,
    loadMore,
    refetch,
    duplicateCanvas,
    updateCanvas,
    deleteCanvas,
  } = useCanvas({ projectId: projectUuid, limit: PAGE_SIZE })

  const { projects, isLoading: projectsLoading } = useSharedProjects()

  // Find the current project
  const currentProject = projects.find(p => p.uuid === projectUuid)

  const handleRename = React.useCallback(async (canvasId: string, newTitle: string) => {
    await updateCanvas(canvasId, { title: newTitle })
  }, [updateCanvas])

  // A session moved elsewhere no longer matches this page's query, so re-ask the server
  const handleMove = React.useCallback(async (canvasId: string, projectId: string | null) => {
    await updateCanvas(canvasId, { projectId })
    await refetch()
  }, [updateCanvas, refetch])

  const handleRemoveFromProject = React.useCallback(async (canvasId: string) => {
    await updateCanvas(canvasId, { projectId: null })
    await refetch()
  }, [updateCanvas, refetch])

  const {
    handleAction,
    closeModal,
    modalHandlers,
    modalProps,
  } = useCanvasModals({
    projects,
    onRename: handleRename,
    onMove: handleMove,
    onDelete: deleteCanvas,
    onDuplicate: duplicateCanvas,
    onRemoveFromProject: handleRemoveFromProject,
  })

  if (projectsLoading || canvasesLoading) {
    return (
      <main className="h-full overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="w-full space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(6)].map((_, index) => (
                <CanvasCardSkeleton key={`skeleton-${index}`} />
              ))}
            </div>
          </div>
        </div>
      </main>
    )
  }

  if (!currentProject) {
    return (
      <main className="h-full overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-center">
              <Icon name="folder-x" className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-sm font-semibold">Project not found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                The project you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.
              </p>
              <Link to="/workspace" className="mt-4 inline-block">
                <Button variant="default" size="sm">
                  <Icon name="arrow-left" className="mr-1 h-4 w-4" />
                  Back to Workspace
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="h-full overflow-hidden">
      <div className="h-full overflow-y-auto">
        <div className="w-full space-y-6">

          {/* Error Display */}
          {canvasesError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load canvases: {canvasesError}
              </AlertDescription>
            </Alert>
          )}

          {/* Canvas Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {projectCanvases.map((canvas, index) => (
              <CanvasCard
                key={canvas.id}
                canvas={canvas}
                contextMenuType="project"
                onAction={handleAction}
                showProject={false}
                index={index}
              />
            ))}
          </div>

          {/* True count for the project, plus the next page */}
          <CanvasListFooter
            loaded={projectCanvases.length}
            total={total}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />

          {/* Empty State */}
          {projectCanvases.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
            <NotFound
              title="No sessions"
              description="Create a new session in this project to get started"
              buttonText="Create New Session"
              buttonLink={`/canvas/new?projectId=${projectUuid}`}
            />
            </motion.div>
          )}

          <CanvasActionModals
            modalProps={modalProps}
            modalHandlers={modalHandlers}
            onClose={closeModal}
          />
        </div>
      </div>
    </main>
  )
}
